import { Popover } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { getBlueprints } from 'app/plugins/openstack/components/infrastructure/actions'
import GenerateSupportBundleModal from 'app/plugins/openstack/components/infrastructure/hosts/GenerateSupportBundleModal'
import clsx from 'clsx'
import DocumentMeta from 'core/components/DocumentMeta'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Progress from 'core/components/progress/Progress'
import Text from 'core/elements/Text'
import Badge from 'core/elements/badge'
import Tooltip from 'core/elements/tooltip'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import EllipsisText from 'k8s/components/common/EllipsisText'
import IconButton from 'pf9-ui-components/built/elements/button/IconButton'
import { path } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { EnabledOrDisabled, modeLabels } from '../ClusterHostGroupsListPage'
import DeleteClusterHostGroupsDialog from '../DeleteClusterHostGroupsDialog'
import EditClusterHostGroupModal from '../EditClusterHostGroupModal'
import { getClusterHostGroupBadgeVariant } from '../helpers'
import { IClusterHostGroup } from '../model'
import { VMHAStatusForClusterList } from '../VMHAStatusCell'

interface Props {
  clusterHostGroup: IClusterHostGroup
}

const ClusterHostGroupOverview = ({ clusterHostGroup }: Props) => {
  const classes = useStyles({})
  const [openSettingPopover, setOpenSettingPopover] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const popoverRef = React.useRef(null)
  const [showDeleteClusterHostGroupsDialog, setShowDeleteClusterHostGroupsDialog] = useState<
    boolean
  >(false)
  const [showEditClusterHostGroupModal, setShowEditClusterHostGroupModal] = useState<boolean>(false)
  const [showGenerateSupportBundleModal, setShowGenerateSupportBundleModal] = useState<boolean>(
    false,
  )
  const [blueprintDetails, setBlueprintDetails] = useState<any>({})
  const [loadingBlueprints, setLoadingBlueprints] = useState<boolean>(true)

  const handleSettingsClick = useCallback((event) => {
    setOpenSettingPopover(true)
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setOpenSettingPopover(false)
    setAnchorEl(null)
  }, [])

  const handleDeleteCluster = useCallback(() => {
    if (clusterHostGroup?.hostlist?.length > 0) {
      return
    }
    setOpenSettingPopover(false)
    setShowDeleteClusterHostGroupsDialog(true)
  }, [clusterHostGroup])

  const handleEditCluster = useCallback(() => {
    setOpenSettingPopover(false)
    setShowEditClusterHostGroupModal(true)
  }, [])

  const handleGenerateSupportBundle = useCallback(() => {
    setOpenSettingPopover(false)
    setShowGenerateSupportBundleModal(true)
  }, [])

  useEffect(() => {
    const getClusterBlueprints = async () => {
      try {
        setLoadingBlueprints(true)
        const blueprints = (await getBlueprints()) as any[]
        setBlueprintDetails(blueprints?.[0] || {})
      } catch (error) {
        setBlueprintDetails({})
      } finally {
        setLoadingBlueprints(false)
      }
    }

    getClusterBlueprints()
  }, [])

  const overviewBody = useMemo(
    () => [
      {
        label: 'BLUEPRINT',
        key: 'blueprintName',
        Component: () => (
          <Progress loading={loadingBlueprints} inline>
            <Text variant="caption1">{blueprintDetails?.name || '_'}</Text>
          </Progress>
        ),
      },
      {
        label: 'HOSTS',
        key: 'hosts.length',
        defaultValue: '0',
      },
      {
        label: 'VIRTUALIZED GUEST CPU MODE',
        key: 'cpu.mode',
        Component: ({ value }) => <Text variant="caption1">{modeLabels[value] || 'Default'}</Text>,
      },
      {
        label: 'VIRTUALIZED GUEST CPU MODEL',
        key: 'cpu.model',
        cond: ({ item }) => item?.cpu?.mode === 'custom',
      },
      {
        label: 'VM HIGH AVAILABILITY',
        key: 'vmhaStatus',
        Component: ({ value }) => (
          <VMHAStatusForClusterList value={value} item={clusterHostGroup} />
        ),
      },
      {
        label: 'DYNAMIC RESOURCE REBALANCING',
        key: 'autoResourceRebalancing.enabled',
        labelsTooltipDescription: 'DYNAMIC RESOURCE REBALANCING',
        Component: EnabledOrDisabled,
      },
    ],
    [blueprintDetails, clusterHostGroup],
  )

  return (
    <>
      <DocumentMeta title="Cluster Details" breadcrumbs />
      {showDeleteClusterHostGroupsDialog && (
        <DeleteClusterHostGroupsDialog
          rows={[clusterHostGroup]}
          onClose={() => {
            setShowDeleteClusterHostGroupsDialog(false)
          }}
        />
      )}
      {showEditClusterHostGroupModal && (
        <EditClusterHostGroupModal
          onClose={() => setShowEditClusterHostGroupModal(false)}
          rows={[clusterHostGroup]}
        />
      )}
      {showGenerateSupportBundleModal && (
        <GenerateSupportBundleModal
          onClose={() => setShowGenerateSupportBundleModal(false)}
          rows={[clusterHostGroup]}
        />
      )}
      <div className={classes.cardContainer}>
        <div className={classes.cardHeader}>
          <div className={classes.cardContent}>
            <div className={classes.badge}>
              <FontAwesomeIcon light size="lg" className="fa-regular">
                server
              </FontAwesomeIcon>
            </div>
            <Text variant="caption1" className={clsx(classes.headerText, classes.headerSubText)}>
              CONFIGURATION OVERVIEW
            </Text>
          </div>
          {/* Cluster Settings */}
          {!isReadonlyUser() && (
            <IconButton
              size="md"
              icon="cog"
              solid={false}
              className={classes.settingsMenuButton}
              onClick={handleSettingsClick}
              info="Cluster Actions"
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          )}
          {/* Settings Menu */}
          <Popover
            open={openSettingPopover}
            onClose={handleClose}
            anchorEl={anchorEl}
            ref={popoverRef}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <div className={classes.settingsMenuContainer}>
              <Tooltip message="Cannot delete a cluster with hosts assigned">
                <Text
                  variant="body2"
                  className={clsx(
                    classes.settingsMenuItem,
                    clusterHostGroup?.hostlist?.length && classes.disabled,
                  )}
                  onClick={handleDeleteCluster}
                >
                  <FontAwesomeIcon
                    size="md"
                    className={clusterHostGroup?.hostlist?.length && classes.disabled}
                  >
                    trash-can
                  </FontAwesomeIcon>
                  Delete Cluster
                </Text>
              </Tooltip>
              <Tooltip message="Edit cluster configuration">
                <Text
                  variant="body2"
                  className={clsx(classes.settingsMenuItem)}
                  onClick={handleEditCluster}
                >
                  <FontAwesomeIcon size="md">edit</FontAwesomeIcon>
                  Edit Cluster
                </Text>
              </Tooltip>
              <Tooltip message="Generate support bundle">
                <Text
                  variant="body2"
                  className={clsx(classes.settingsMenuItem)}
                  onClick={handleGenerateSupportBundle}
                >
                  <FontAwesomeIcon size="md">block-question</FontAwesomeIcon>
                  Generate Support Bundle
                </Text>
              </Tooltip>
            </div>
          </Popover>
        </div>
        <div className={clsx(classes.cardContent, classes.content)}>
          <EllipsisText
            variant="subtitle1"
            title={clusterHostGroup?.name}
            maxWidth={230}
            showTooltip
          >
            {clusterHostGroup?.name}
          </EllipsisText>
          <div className={classes.headerSubText}>
            <Badge
              variant={getClusterHostGroupBadgeVariant(clusterHostGroup?.status)}
              text={clusterHostGroup?.status}
            />
          </div>
        </div>
        <div className={classes.cardsRow}>
          {overviewBody.map(({ key, label, defaultValue, cond, Component }, index) => {
            if (cond && !cond({ item: clusterHostGroup })) {
              return null
            }
            const value = path(key.split('.'), clusterHostGroup) || defaultValue
            return (
              <div key={`key-${index}`}>
                <Text variant="body2" className={clsx(classes.headerText, classes.customText)}>
                  {label}
                </Text>
                {Component ? (
                  <Component value={value} />
                ) : (
                  <div className={classes.cardContent}>
                    <Text variant="caption1">{value as string}</Text>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

export default ClusterHostGroupOverview

const useStyles = makeStyles((theme: Theme) => ({
  cardsRow: {
    display: 'grid',
    marginTop: theme.spacing(1),
    gridColumnGap: theme.spacing(4),
    gridRowGap: theme.spacing(3),
    alignItems: 'center',
    paddingTop: theme.spacing(6),
  },
  cardContainer: {
    backgroundColor: theme.components.card.background,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '4px 4px 4px 4px',
    padding: theme.spacing(3),
  },
  headerText: {
    color: theme.components.card.passiveText,
  },
  customText: {
    fontSize: '12px',
    paddingBottom: theme.spacing(1),
  },
  headerSubText: {
    paddingLeft: theme.spacing(1),
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    backgroundColor: colors.blue[900],
    borderRadius: '4px 4px 4px 4px',
    padding: theme.spacing(0.5),
    '& i': {
      color: 'white',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    paddingTop: theme.spacing(2),
  },
  settingsMenuButton: {
    marginLeft: 'auto',
  },
  settingsMenuContainer: {
    padding: theme.spacing(1, 0),
    width: '220px',
  },
  settingsMenuItem: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    alignItems: 'center',
    gap: 6,
    padding: theme.spacing(1, 2),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  disabled: {
    cursor: 'not-allowed',
    color: theme.components.iconButton.disabledColor,
  },
}))
